// App-wide timezone for all "what calendar day is it" logic.
//
// The cron poller and API routes run on Vercel in UTC. Reservation dates
// (checkin_date / checkout_date) are stored as plain calendar dates, so
// classifying them with the server's UTC clock lands the day-before reminder
// window — and the inbound confirmation matcher — on the wrong calendar day
// for Eastern-time properties. Evaluating day boundaries in this timezone
// fixes that. "America/New_York" tracks EST/EDT automatically.
export const APP_TIMEZONE = "America/New_York";

// YYYY-MM-DD for the given instant, evaluated in the app timezone.
export function appDateKey(d: Date = new Date()): string {
  // en-CA formats as YYYY-MM-DD.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: APP_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

// YYYY-MM-DD n days from the given instant's app-timezone calendar date.
// Date arithmetic is done in UTC so it can't be skewed by the server's local
// DST shifts; only the starting calendar date is timezone-resolved.
export function appDatePlusDays(n: number, d: Date = new Date()): string {
  const [y, m, day] = appDateKey(d).split("-").map(Number);
  const r = new Date(Date.UTC(y, m - 1, day));
  r.setUTCDate(r.getUTCDate() + n);
  return `${r.getUTCFullYear()}-${String(r.getUTCMonth() + 1).padStart(2, "0")}-${String(r.getUTCDate()).padStart(2, "0")}`;
}

// Hour-of-day (0-23) of the given instant evaluated in the app timezone.
// Use anywhere classification depends on the current wall-clock hour in NY
// (e.g. greeting, "is it past 9am yet" gates).
export function appHour(d: Date = new Date()): number {
  const h = new Intl.DateTimeFormat("en-US", {
    timeZone: APP_TIMEZONE,
    hour: "2-digit",
    hourCycle: "h23",
  }).format(d);
  return Number(h);
}

// Minute-of-hour (0-59) of the given instant evaluated in the app timezone.
// Pairs with appHour() to get the current NY wall-clock time to the minute.
export function appMinute(d: Date = new Date()): number {
  const m = new Intl.DateTimeFormat("en-US", {
    timeZone: APP_TIMEZONE,
    minute: "2-digit",
  }).format(d);
  return Number(m);
}

// Parse a 12-hour clock string ("4:00 PM") into 24-hour {hour, minute}.
// Mirrors the parser in lib/twilio.ts but lives here so non-Twilio callers
// (the poller's check-in-passed logic) don't pull in the Twilio SDK. Falls
// back to noon on an unrecognised format (matching twilio.ts).
export function parseClockTime(time: string): { hour: number; minute: number } {
  const match = time?.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return { hour: 12, minute: 0 };
  let hour = parseInt(match[1], 10);
  const minute = parseInt(match[2], 10);
  const period = match[3].toUpperCase();
  if (period === "PM" && hour !== 12) hour += 12;
  if (period === "AM" && hour === 12) hour = 0;
  return { hour, minute };
}

// Anchor a bare "YYYY-MM-DD" calendar date at noon UTC so it renders as the
// intended day in any timezone (always safe after the -4/-5h NY shift).
// Use when you need a Date object for that calendar day (sorting, range
// math); for display, pass the bare string directly to format-date helpers.
export function parseAppDate(s: string): Date {
  return new Date(`${s.slice(0, 10)}T12:00:00Z`);
}

// UTC instant of the start (00:00) of the given calendar date in the app
// timezone. Use to compare timestamptz columns ("rows since NY midnight").
// Defaults to "today in NY".
export function appDayStartInstant(dayKey: string = appDateKey()): Date {
  // Probe noon UTC on the target date, ask Intl for its NY wall components,
  // diff to discover the NY offset, then back out the UTC instant for 00:00 NY.
  const probe = new Date(`${dayKey}T12:00:00Z`);
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone: APP_TIMEZONE,
      hourCycle: "h23",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    })
      .formatToParts(probe)
      .map((p) => [p.type, p.value]),
  );
  const wallUtc = Date.UTC(
    Number(parts.year), Number(parts.month) - 1, Number(parts.day),
    Number(parts.hour), Number(parts.minute), Number(parts.second),
  );
  const offMin = Math.round((wallUtc - probe.getTime()) / 60_000);
  // Midnight NY in UTC = midnight UTC on dayKey, shifted by the NY offset.
  return new Date(Date.UTC(
    Number(dayKey.slice(0, 4)), Number(dayKey.slice(5, 7)) - 1, Number(dayKey.slice(8, 10)),
  ) - offMin * 60_000);
}
